import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { SeriesPoint, Summary, SystemPoint } from '../api/types';
import { Chart } from '../components/Chart';
import { bps, bytes } from '../lib/format';
import { useApp } from '../stores/app';

const RANGES = [
  { label: '1 час', ms: 3_600_000 },
  { label: '24 часа', ms: 86_400_000 },
  { label: '7 дней', ms: 7 * 86_400_000 },
];

export function Dashboard() {
  const { tokenFilter, setTokenFilter, tokens } = useApp();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [points, setPoints] = useState<SeriesPoint[]>([]);
  const [system, setSystem] = useState<SystemPoint[]>([]);
  const [usage, setUsage] = useState<Array<{ tokenId: string; sessions: number; streams: number; bytesOut: number }>>([]);
  const [rangeMs, setRangeMs] = useState(RANGES[0]!.ms);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const to = Date.now();
        const from = to - rangeMs;
        const [s, series, sys, tokenStats] = await Promise.all([
          api.summary(tokenFilter || undefined),
          api.series(from, to, tokenFilter || undefined),
          api.systemSeries(from, to),
          api.tokenUsage(from, to),
        ]);
        if (cancelled) return;
        setSummary(s);
        setPoints(series.points);
        setSystem(sys.points);
        setUsage(tokenStats.items);
        setError(null);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    };

    void load();
    const timer = setInterval(() => void load(), 5000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [rangeMs, tokenFilter]);

  return (
    <>
      <div className="topbar">
        <h1>Дашборд</h1>
        <select
          className="shrink"
          style={{ width: 220 }}
          value={tokenFilter}
          onChange={(e) => setTokenFilter(e.target.value)}
        >
          <option value="">Все токены</option>
          {tokens.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} ({t.id})
            </option>
          ))}
        </select>
        <select className="shrink" style={{ width: 130 }} value={rangeMs} onChange={(e) => setRangeMs(Number(e.target.value))}>
          {RANGES.map((r) => (
            <option key={r.ms} value={r.ms}>
              {r.label}
            </option>
          ))}
        </select>
      </div>

      {error && <div className="panel error-text">{error}</div>}

      <div className="tiles">
        <div className="tile">
          <div className="label">Активных потоков</div>
          <div className="value">{summary?.streamsActive ?? '—'}</div>
          <div className="sub">
            {Object.entries(summary?.modes ?? {})
              .map(([mode, n]) => `${mode}: ${n}`)
              .join(' · ') || 'нет данных'}
          </div>
        </div>
        <div className="tile">
          <div className="label">Зрителей</div>
          <div className="value">{summary?.viewersActive ?? '—'}</div>
          <div className="sub">свежесть 15 с</div>
        </div>
        <div className="tile">
          <div className="label">Исходящий</div>
          <div className="value">{summary ? summary.outMbps : '—'}</div>
          <div className="sub">Мбит/с, среднее за минуту</div>
        </div>
        <div className="tile">
          <div className="label">Входящий</div>
          <div className="value">{summary ? summary.inMbps : '—'}</div>
          <div className="sub">Мбит/с, от регистраторов</div>
        </div>
        <div className="tile">
          <div className="label">CPU</div>
          <div className="value">{summary?.system ? `${summary.system.cpuPct}%` : '—'}</div>
          <div className="sub">
            {summary?.system ? `RAM ${bytes(summary.system.memUsed)} из ${bytes(summary.system.memTotal)}` : ''}
          </div>
        </div>
      </div>

      <div className="grid-2">
        <div className="panel">
          <h2>Зрители и трафик</h2>
          <Chart
            series={[
              { label: 'Зрители', color: 'var(--accent)', values: points.map((p) => p.viewers) },
              {
                label: 'Исходящий',
                color: 'var(--ok)',
                values: points.map((p) => p.bytesOut),
                format: (v) => bytes(v),
              },
            ]}
          />
        </div>

        <div className="panel">
          <h2>Система</h2>
          <Chart
            series={[
              { label: 'CPU, %', color: 'var(--warn)', values: system.map((p) => p.cpuPct) },
              {
                label: 'Исходящий канал',
                color: 'var(--accent)',
                values: system.map((p) => p.netOutBps),
                format: (v) => bps(v),
              },
            ]}
          />
        </div>
      </div>

      <div className="panel">
        <h2>По токенам</h2>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Токен</th>
                <th className="num">Сессий</th>
                <th className="num">Потоков</th>
                <th className="num">Отдано</th>
              </tr>
            </thead>
            <tbody>
              {usage.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted">
                    За выбранный период обращений не было.
                  </td>
                </tr>
              )}
              {usage.map((u) => {
                const token = tokens.find((t) => t.id === u.tokenId);
                return (
                  <tr key={u.tokenId ?? 'none'}>
                    <td>
                      {token?.name ?? u.tokenId ?? '—'} <span className="muted mono">{u.tokenId}</span>
                    </td>
                    <td className="num">{u.sessions}</td>
                    <td className="num">{u.streams}</td>
                    <td className="num">{bytes(u.bytesOut ?? 0)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
